import { MouseEvent, PointerEvent, useRef } from 'react';

// Past this many pixels sideways a drag pages the days; anything shorter, or mostly vertical, is a tap or a scroll.
const SWIPE_THRESHOLD_PX = 40;

/**
 * Pointer handlers that page the days on a horizontal swipe. Spread them on the swiped element and give
 * it `touch-pan-y`: with the browser's default touch action it claims the sideways pan itself and
 * cancels the pointer before it lifts.
 */
export const useSwipePaging = ({
  onSwipeLeft,
  onSwipeRight
}: {
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
}) => {
  const start = useRef<{ x: number; y: number } | null>(null);
  const didSwipe = useRef(false);

  return {
    onPointerDown: (event: PointerEvent<HTMLElement>) => {
      start.current = { x: event.clientX, y: event.clientY };
      didSwipe.current = false;
    },
    onPointerUp: (event: PointerEvent<HTMLElement>) => {
      const from = start.current;
      start.current = null;
      if (!from) {
        return;
      }
      const dx = event.clientX - from.x;
      const dy = event.clientY - from.y;
      if (Math.abs(dx) > SWIPE_THRESHOLD_PX && Math.abs(dx) > Math.abs(dy)) {
        didSwipe.current = true;
        if (dx < 0) {
          onSwipeLeft();
        } else {
          onSwipeRight();
        }
      }
    },
    onPointerCancel: () => {
      start.current = null;
    },
    // A mouse drag that ends on the window or tab it began on still clicks it, after the swipe already paged.
    onClickCapture: (event: MouseEvent<HTMLElement>) => {
      if (didSwipe.current) {
        didSwipe.current = false;
        event.stopPropagation();
      }
    }
  };
};
